<template>
  <div class="workspace-page artist-catalog">
    <header class="catalog-heading">
      <div>
        <p class="workspace-eyebrow">
          {{ $t('catalog.eyebrow') }}
        </p><h1>{{ $t('catalog.title') }} <span>{{ $t('catalog.title-accent') }}</span></h1><p>{{ $t('catalog.description') }}</p>
      </div>
      <router-link
        :to="user ? '/discover' : { path: '/auth/login', query: { redirect: '/discover' } }"
        class="catalog-discover-link"
      >
        <GrowthIcon name="music" /><span>{{ $t('catalog.listen-instead') }}</span><GrowthIcon name="arrow" />
      </router-link>
    </header>

    <form
      class="catalog-search-panel"
      role="search"
      :aria-label="$t('catalog.search-label')"
      @submit.prevent="commitSearch"
    >
      <div class="catalog-search">
        <label for="catalog-artist-search">{{ $t('catalog.search-label') }}</label><div>
          <GrowthIcon name="people" /><input
            id="catalog-artist-search"
            v-model="searchText"
            type="search"
            :placeholder="$t('artists-catalog.filters.search-placeholder')"
            @input="scheduleSearch"
          ><button
            v-if="searchText"
            type="button"
            class="catalog-search-clear"
            :aria-label="$t('catalog.clear-search')"
            @click="updateFilters({ search: '' })"
          >
            <GrowthIcon name="close" />
          </button>
        </div>
      </div>
      <label class="catalog-filter">{{ $t('artists-catalog.filters.genre') }}<select
        :value="filters.genre"
        @change="updateFilters({ genre: $event.target.value })"
      ><option value="">{{ $t('artists-catalog.filters.all-genres') }}</option><option
        v-for="genre in availableGenres"
        :key="genre"
        :value="genre"
      >{{ genre }}</option></select></label>
      <label class="catalog-filter">{{ $t('artists-catalog.filters.country') }}<select
        :value="filters.country"
        @change="updateFilters({ country: $event.target.value })"
      ><option value="">{{ $t('artists-catalog.filters.all-countries') }}</option><option
        v-for="country in availableCountries"
        :key="country.code"
        :value="country.code"
      >{{ country.flag }} {{ country.name }}</option></select></label>
    </form>
    <div
      v-if="hasFilters"
      class="catalog-filter-chips"
      :aria-label="$t('catalog.active-filters')"
    >
      <button
        v-if="filters.search"
        @click="updateFilters({ search: '' })"
      >
        {{ filters.search }}<GrowthIcon name="close" /><span class="sr-only">{{ $t('catalog.clear-search') }}</span>
      </button><button
        v-if="filters.genre"
        @click="updateFilters({ genre: '' })"
      >
        {{ filters.genre }}<GrowthIcon name="close" /><span class="sr-only">{{ $t('catalog.clear-genre') }}</span>
      </button><button
        v-if="filters.country"
        @click="updateFilters({ country: '' })"
      >
        <CountryLabel :value="filters.country" /><GrowthIcon name="close" /><span class="sr-only">{{ $t('catalog.clear-country') }}</span>
      </button><button
        class="catalog-clear-all"
        @click="updateFilters({ search: '', country: '', genre: '' })"
      >
        {{ $t('artists-catalog.filters.clear') }}
      </button>
    </div>

    <section
      ref="resultsSection"
      class="catalog-results"
      :aria-busy="loading || debouncing"
      aria-labelledby="catalog-results-title"
    >
      <header class="catalog-results-heading">
        <div>
          <h2 id="catalog-results-title">
            {{ $t(hasFilters ? 'catalog.results-title' : 'catalog.browse-title') }}
          </h2><p
            v-if="loading || debouncing"
            role="status"
          >
            {{ $t('artists-catalog.updating') }}
          </p><p
            v-else-if="!error"
            aria-live="polite"
          >
            {{ $t('catalog.result-count', { count: n(total) }, total) }}
          </p>
        </div><div
          class="catalog-sort"
          role="group"
          :aria-label="$t('artists-catalog.filters.sort-by')"
        >
          <button
            v-for="option in sorts"
            :key="option.value"
            :class="{ active: filters.sort === option.value }"
            :aria-pressed="filters.sort === option.value"
            @click="updateFilters({ sort: option.value })"
          >
            {{ $t(option.label) }}
          </button>
        </div>
      </header>
      <div
        v-if="error"
        class="product-message"
        role="alert"
      >
        <p>{{ $t('catalog.load-error') }}</p><button @click="loadArtists">
          {{ $t('general.retry') }}
        </button>
      </div>
      <div
        v-else-if="loading"
        class="catalog-grid catalog-skeleton-grid"
        aria-hidden="true"
      >
        <div
          v-for="item in 8"
          :key="item"
          class="catalog-skeleton"
        >
          <span /><i /><i />
        </div>
      </div>
      <div
        v-else-if="!artists.length"
        class="product-empty catalog-empty"
      >
        <GrowthIcon name="people" /><h3>{{ $t('artists-catalog.empty.title') }}</h3><p>{{ $t('artists-catalog.empty.description') }}</p><button
          v-if="hasFilters"
          class="button is-primary"
          @click="updateFilters({ search: '', country: '', genre: '' })"
        >
          {{ $t('artists-catalog.filters.clear') }}
        </button>
      </div>
      <div
        v-else
        class="catalog-grid"
      >
        <ArtistCard
          v-for="artist in artists"
          :key="artist._id"
          :artist="artist"
          :following="followedIds.has(artist._id)"
          @open="openArtist"
        />
      </div>
      <div
        v-if="!loading && !error && artists.length"
        class="catalog-pagination"
      >
        <p>{{ $t('catalog.showing', { start: n((filters.page - 1) * pageSize + 1), end: n((filters.page - 1) * pageSize + artists.length), total: n(total) }) }}</p><nav
          v-if="totalPages > 1"
          :aria-label="$t('artists-catalog.pagination.aria-label')"
        >
          <BasePagination
            :current-page="filters.page"
            :total-pages="totalPages"
            :total-items="total"
            :per-page="pageSize"
            :show-first-last="false"
            :max-visible="3"
            @page-change="goToPage"
          />
        </nav>
      </div>
    </section>

    <aside class="catalog-growth">
      <div class="catalog-growth-icon">
        <GrowthIcon name="spark" />
      </div><div>
        <p class="workspace-eyebrow">
          {{ $t('catalog.your-next-listener') }}
        </p><h2>{{ $t('catalog.growth-title') }}</h2><p>{{ $t(isPro ? 'catalog.growth-pro-copy' : 'catalog.growth-copy') }}</p>
      </div><div class="catalog-growth-actions">
        <router-link
          v-if="isPro"
          to="/tools/indiefy-me"
          class="growth-button growth-button--lime"
        >
          {{ $t('catalog.shape-page') }}<GrowthIcon name="arrow" />
        </router-link><template v-else>
          <router-link
            to="/upgrade?from=v4_artists-catalog"
            class="growth-button growth-button--lime"
          >
            {{ $t('catalog.explore-pro') }}<GrowthIcon name="arrow" />
          </router-link><router-link
            :to="user ? '/tools/indiefy-me' : { path: '/auth/signup', query: { redirect: '/tools/indiefy-me' } }"
            class="product-text-button"
          >
            {{ $t('catalog.start-page') }}
          </router-link>
        </template>
      </div>
    </aside>
    <div
      v-if="!isPro && !loading && !error && artists.length"
      class="catalog-ad"
    >
      <AdSenseBanner :ad-slot="AD_PLACEMENTS.catalog.slot" />
    </div>

    <CatalogArtistDialog
      v-if="selectedArtist"
      :key="selectedArtist._id"
      :artist="selectedArtist"
      :user="user"
      :is-own-artist="isOwnArtist"
      :following="followedIds.has(selectedArtist._id)"
      :busy="followBusy"
      :checking="checkingFollow"
      :follow-ready="followReady"
      :error="followError"
      :success="followSuccess"
      :login-link="{ path: '/auth/login', query: { redirect: route.fullPath } }"
      @close="selectedArtist = null"
      @toggle-follow="toggleFollow"
      @retry-status="checkFollowStatus"
    />
  </div>
</template>
<script setup>
import { AD_PLACEMENTS } from '@/config/ads';
import { ref, reactive, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useArtistStore } from '@/store/artists';
import { usePageSeo } from '@/composables/usePageSeo';
import publicService from '@/services/publicService';
import artistService from '@/services/artistService';
import feedService from '@/services/feedService';
import { discoveryItems, entityId } from '@/utils/discovery';
import { catalogQuery } from '@/utils/artistCatalog';
import { countryDetails } from '@/utils/countryDisplay';
import countries from '@/data/countries';
import genres from '@/data/genres';
import ArtistCard from '@/components/catalog/ArtistCard.vue';
import CatalogArtistDialog from '@/components/catalog/CatalogArtistDialog.vue';
import GrowthIcon from '@/components/growth/GrowthIcon.vue';
import CountryLabel from '@/components/common/CountryLabel.vue';
import BasePagination from '@/components/ui/BasePagination.vue';
import AdSenseBanner from '@/components/ui/AdSenseBanner.vue';

const { t, n, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const roster = useArtistStore();
const user = computed(() => auth.currentUser?._id ? auth.currentUser : null);
const isPro = computed(() => !!user.value?.subscription);
usePageSeo({ titleKey: 'artists-catalog.seo.title', descriptionKey: 'artists-catalog.seo.description', keywords: computed(() => t('artists-catalog.seo.keywords')) });
const filters = computed(() => catalogQuery(route.query));
const searchText = ref(filters.value.search);
const artists = ref([]);
const total = ref(0);
const pageSize = 20;
const loading = ref(false);
const debouncing = ref(false);
const error = ref(false);
const resultsSection = ref(null);
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize)));
const hasFilters = computed(() => !!(filters.value.search || filters.value.genre || filters.value.country));
const availableGenres = Object.keys(genres).sort();
const availableCountries = computed(() => countries.map(country => countryDetails(country.code, locale.value)).sort((a, b) => a.name.localeCompare(b.name, locale.value)));
const sorts = [{ value: 'followers', label: 'catalog.sort-followers' }, { value: 'newest', label: 'catalog.sort-newest' }, { value: 'oldest', label: 'catalog.sort-oldest' }];
let searchTimer;
let requestVersion = 0;
let catalogRequest;
let alive = true;
let scrollAfterLoad = false;

function makeQuery(next) {
  const query = { ...route.query };
  for (const key of ['search', 'country', 'genre', 'sort', 'page']) delete query[key];
  for (const key of ['search', 'country', 'genre']) if (next[key]) query[key] = next[key];
  if (next.sort !== 'followers') query.sort = next.sort;
  if (next.page > 1) query.page = String(next.page);
  return query;
}
function clearSearchTimer() { clearTimeout(searchTimer); debouncing.value = false; }
function updateFilters(patch) {
  clearSearchTimer();
  const next = { ...filters.value, search: searchText.value.trim(), ...patch, page: 1 };
  searchText.value = next.search;
  router.push({ query: makeQuery(next) });
}
function commitSearch() {
  clearSearchTimer();
  router.replace({ query: makeQuery({ ...filters.value, search: searchText.value.trim(), page: 1 }) });
}
function scheduleSearch() { clearTimeout(searchTimer); debouncing.value = true; searchTimer = setTimeout(commitSearch, 350); }
function goToPage(page) {
  if (loading.value || debouncing.value || page < 1 || page > totalPages.value || page === filters.value.page) return;
  scrollAfterLoad = true;
  router.push({ query: makeQuery({ ...filters.value, page }) });
}
async function loadArtists() {
  const version = ++requestVersion;
  catalogRequest?.abort();
  catalogRequest = new AbortController();
  const selection = { ...filters.value };
  loading.value = true; error.value = false;
  const params = { published: 1, limit: pageSize, page: selection.page - 1, search: selection.search, orderBy: { followers: '-followers', newest: '-createdAt', oldest: 'createdAt' }[selection.sort] };
  if (selection.country) params.country = selection.country;
  if (selection.genre) params.genre = selection.genre;
  try {
    const response = await publicService.getPublicArtists(params, { signal: catalogRequest.signal, timeout: 45000 });
    if (!alive || version !== requestVersion) return;
    if (!Array.isArray(response?.items) || response.total === undefined || response.total === null || !Number.isFinite(Number(response.total)) || Number(response.total) < 0) throw new Error('Invalid catalog response');
    const count = Number(response.total);
    const lastPage = Math.max(1, Math.ceil(count / pageSize));
    total.value = count;
    if (selection.page > lastPage) { await router.replace({ query: makeQuery({ ...selection, page: lastPage }) }); return; }
    artists.value = response.items.filter(artist => artist?._id);
    if (scrollAfterLoad) { scrollAfterLoad = false; await nextTick(); resultsSection.value?.scrollIntoView({ block: 'start', behavior: 'auto' }); }
  } catch { if (alive && version === requestVersion) { error.value = true; artists.value = []; total.value = 0; } }
  finally { if (alive && version === requestVersion) loading.value = false; }
}
watch(() => route.query, () => { clearSearchTimer(); searchText.value = filters.value.search; });
watch(() => JSON.stringify(filters.value), loadArtists, { immediate: true });

const selectedArtist = ref(null);
const followedIds = reactive(new Set());
const checkingFollow = ref(false);
const followReady = ref(false);
const followError = ref(false);
const followSuccess = ref(false);
const followBusy = ref(false);
const isOwnArtist = computed(() => !!user.value && (entityId(selectedArtist.value?.user) === user.value._id || roster.artists.some(artist => artist._id === selectedArtist.value?._id)));
let followPage = 0;
let followSeen = 0;
let followComplete = false;
let followRequest;
let checkVersion = 0;
let followSession = 0;
async function loadFollowPage() {
  if (followRequest) return followRequest;
  const session = followSession;
  followRequest = (async () => {
    const response = await feedService.getFollowedArtists({ page: followPage, limit: 25 });
    if (!alive || session !== followSession) return;
    const items = discoveryItems(response);
    items.forEach(item => { const id = entityId(item.artist) || item._id; if (id) followedIds.add(id); });
    followSeen += items.length; followPage++;
    followComplete = items.length < 25 || (response.total !== undefined && followSeen >= Number(response.total));
  })();
  try { await followRequest; } finally { if (session === followSession) followRequest = null; }
}
function openArtist(artist) { selectedArtist.value = artist; followSuccess.value = false; checkFollowStatus(); }
async function checkFollowStatus() {
  const version = ++checkVersion;
  const artistId = selectedArtist.value?._id;
  checkingFollow.value = true; followReady.value = false; followError.value = false;
  try {
    if (user.value && !isOwnArtist.value) {
      while (alive && version === checkVersion && selectedArtist.value?._id === artistId && !followComplete && !followedIds.has(artistId)) await loadFollowPage();
    }
    if (alive && version === checkVersion) followReady.value = true;
  } catch { if (alive && version === checkVersion) followError.value = true; }
  finally { if (alive && version === checkVersion) checkingFollow.value = false; }
}
async function toggleFollow() {
  if (!user.value?.email || isOwnArtist.value || !selectedArtist.value || !followReady.value || followBusy.value) return;
  const artistId = selectedArtist.value._id;
  const following = followedIds.has(artistId);
  const session = followSession;
  followBusy.value = true; followError.value = false; followSuccess.value = false;
  try {
    if (following) await artistService.unfollowArtist(artistId, user.value.email);
    else {
      const payload = { email: user.value.email, source: 'catalog' };
      const defaultArtist = roster.getDefaultArtist();
      if (defaultArtist?._id) payload.artist = defaultArtist._id;
      await artistService.followArtist(artistId, payload);
    }
    if (!alive || session !== followSession) return;
    if (following) followedIds.delete(artistId); else followedIds.add(artistId);
    // Membership changes can shift every following page after the changed row.
    followPage = 0; followSeen = 0; followComplete = false;
    followSuccess.value = true;
  } catch { if (alive && session === followSession) followError.value = true; }
  finally { if (alive && session === followSession) followBusy.value = false; }
}
watch(() => user.value?._id, () => {
  followSession++; checkVersion++; followedIds.clear(); followPage = 0; followSeen = 0; followComplete = false; followRequest = null;
  selectedArtist.value = null; followBusy.value = false;
});
onBeforeUnmount(() => { alive = false; requestVersion++; checkVersion++; catalogRequest?.abort(); clearSearchTimer(); });
</script>
<style src="../styles/artist-catalog.css"></style>
